import { action, computed, observable } from 'mobx'
import { pattern, required, validationContext } from 'validx'
import {
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  validateObject,
} from '@/utils/validx-util'
import { ContactInfo } from '@/features/jobs/info-gathering/ContactInfo'
import { PersonalInfoEditorViewModel } from './PersonalInfoEditorViewModel'
import type { SpousalProfileVersion } from './SpousalProfileVersion'

/**
 * Editor for a spousal profile.
 */
export class SpousalProfileEditorViewModel {
  readonly personalInfoVm = new PersonalInfoEditorViewModel()

  @observable private accessor _email = ''
  @observable private accessor _occupation = ''

  readonly validation = validationContext(this, {
    personalInfoVm: [validateObject()],
    email: [
      required('Email is required'),
      pattern('email', 'Must be a valid email'),
    ],
    occupation: [
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(100, 'Occupation must not exceed 100 characters'),
    ],
  })

  @computed get email() {
    return this._email
  }

  set email(value: string) {
    this._email = value
    this.validation.clearErrors('email')
  }

  @computed get occupation() {
    return this._occupation
  }

  set occupation(value: string) {
    this._occupation = value
    this.validation.clearErrors('occupation')
  }

  /**
   * Resets the editor to the given profile, or to defaults.
   */
  @action.bound
  reset(profile?: SpousalProfileVersion | null) {
    this.personalInfoVm.reset(profile?.personalInfo)
    this.email = profile?.contactInfo.email ?? ''
    this.occupation = profile?.occupation ?? ''
    this.validation.reset()
  }

  /**
   * Maps the editor's email to {@link ContactInfo}; spouses only provide an
   * email.
   */
  toContactInfo(): ContactInfo {
    return { ...ContactInfo.empty, email: this.email }
  }
}
